import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";

import {
  Row,
  Col,
  Form,
  Card,
  Spinner,
  Container,
  Table,
  Button,
  Modal,
  ModalHeader,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import {
  ADMIN_PLATFORM_ANALYTICS_STUDENT_PAYMENTS_API,
  PURCHASE_API,
} from "../../../api/api";

import { FiDownload } from "react-icons/fi";
import { FaEye, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// latestIcons
import totalStudents from "../../../assets/icons/admin/enrolled-students.svg";
import totalCoursePurcha from "../../../assets/icons/admin/total-course-purcha.svg";
import weeklyCoursePurchas from "../../../assets/icons/admin/weekly-course-purchas.svg";
import totalRevenue from "../../../assets/icons/tutor/total-earnings.svg";

import excelImg from "../../../assets/excel-icon.png";
import Nofound from "../../../assets/not-found.png";
import Logo from "../../../assets/Skilledition-logo.png";

import { getPageNumbers } from "../../../utils/pagination";
import { exportToExcel } from "../../../utils/exportExcel";
import { handleApiError } from "../../../utils/apiErrorHandler";
import { useSortableTable } from "../../../hooks/useSortableTable";

import "./AdminStudentPayments.css";

const AdminStudentPayments = () => {
  const navigate = useNavigate();

  const [paymentStats, setPaymentStats] = useState({});
  const [studentPayments, setStudentPayments] = useState([]);

  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [downloadInvoice, setDownloadInvoice] = useState(null);

  const invoiceModalRef = useRef(null);
  const invoicePdfRef = useRef(null);
  // If user clicks multiple times quickly:
  const [pdfLoading, setPdfLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  const [paymentCourseFilter, setPaymentCourseFilter] = useState("All Courses");
  const [selectedStudentFilter, setSelectedStudentFilter] =
    useState("All Students");
  const [selectedPaymentStatus, setSelectedPaymentStatus] =
    useState("All Payments");

  // NEW DATE FILTER STATES
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  //loading
  const [loadingStudentPayments, setLoadingStudentPayments] = useState(true);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      setLoadingStudentPayments(false);
      navigate("/admin-signin");

      return;
    }

    const fetchStudentPayments = async () => {
      try {
        const response = await axios.get(
          ADMIN_PLATFORM_ANALYTICS_STUDENT_PAYMENTS_API.STUDENT_PURCHASE_LIST,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = response.data;

        if (data.statusCode === 200) {
          setPaymentStats(data.stats || {});
          setStudentPayments(data.data || []);
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error("Error payments:", error);
        // Server responded with non-2xx status (like 400, 500)

        // CHECK (expiry / invalid token)
        handleApiError(error, navigate, showErrorToast, "/admin-signin");
      } finally {
        setLoadingStudentPayments(false);
      }
    };

    fetchStudentPayments();
  }, [navigate]);

  const fetchInvoiceData = async (studentCourseId) => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/admin-signin");

      return;
    }

    try {
      const response = await axios.post(
        PURCHASE_API.GET_PURCHASE_INVOICE,
        { student_course_id: studentCourseId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        return data.data;
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error fetching invoice:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/admin-signin");
    }
  };

  const handleInvoiceView = async (studentCourseId) => {
    const invoiceData = await fetchInvoiceData(studentCourseId);

    if (invoiceData) {
      setSelectedInvoice(invoiceData);
      setIsInvoiceModalOpen(true);
    }
  };

  // const handleInvoiceDownload = async (studentCourseId) => {
  //   const invoiceData = await fetchInvoiceData(studentCourseId);
  //   if (!invoiceData) return;

  //   setDownloadInvoice(invoiceData);

  //   setTimeout(async () => {
  //     if (!invoicePdfRef.current) return;

  //     const canvas = await html2canvas(invoicePdfRef.current, {
  //       scale: 2,
  //       useCORS: true,
  //       backgroundColor: "#ffffff",
  //     });

  //     const imgData = canvas.toDataURL("image/png");
  //     const pdf = new jsPDF("p", "mm", "a4");

  //     const imgWidth = 210;
  //     const imgHeight = (canvas.height * imgWidth) / canvas.width;

  //     pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
  //     pdf.save(`Invoice-${invoiceData.order_id}.pdf`);

  //     setDownloadInvoice(null);
  //   }, 200);
  // };

  //  convert DD-MM-YYYY → Date

  const handleInvoiceDownload = async (studentCourseId) => {
    if (pdfLoading) return;

    setPdfLoading(true);

    const invoiceData = await fetchInvoiceData(studentCourseId);
    if (!invoiceData) {
      setPdfLoading(false);
      return;
    }

    setDownloadInvoice(invoiceData);
  };

  useEffect(() => {
    const generatePDF = async () => {
      if (!downloadInvoice || !invoicePdfRef.current) return;

      // wait one frame (important for heavy DOM)
      await new Promise(requestAnimationFrame);

      const canvas = await html2canvas(invoicePdfRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");

      const imgWidth = 210;
      const pageHeight = 297;

      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);

      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Invoice-${downloadInvoice.order_id}.pdf`);

      setPdfLoading(false);
      setDownloadInvoice(null);
    };

    generatePDF();
  }, [downloadInvoice]);

  const parseDate = (dateStr) => {
    if (!dateStr) return null;

    const [day, month, year] = dateStr.split("-");

    return new Date(year, month - 1, day);
  };

  const filteredCoursesForDropdown = useMemo(() => {
    return [...new Set(studentPayments.map((s) => s.course_title))];
  }, [studentPayments]);

  const filteredStudentsForDropdown = useMemo(() => {
    return [...new Set(studentPayments.map((s) => s.full_name))];
  }, [studentPayments]);

  const filteredPaymentStatusForDropdown = useMemo(() => {
    return [...new Set(studentPayments.map((s) => s.status))];
  }, [studentPayments]);

  const filteredStudentsPayments = useMemo(() => {
    return studentPayments.filter((s) => {
      const matchCourse =
        paymentCourseFilter === "All Courses" ||
        s.course_title === paymentCourseFilter;

      const matchStudent =
        selectedStudentFilter === "All Students" ||
        s.full_name === selectedStudentFilter;

      const matchStatus =
        selectedPaymentStatus === "All Payments" ||
        s.status === selectedPaymentStatus;

      const itemDate = parseDate(s.submitted_at);
      const from = fromDate ? new Date(fromDate) : null;
      const to = toDate ? new Date(`${toDate}T23:59:59`) : null;

      const matchFrom = !from || (itemDate && itemDate >= from);
      const matchTo = !to || (itemDate && itemDate <= to);

      return matchCourse && matchStudent && matchStatus && matchFrom && matchTo;
    });
  }, [
    studentPayments,
    paymentCourseFilter,
    selectedStudentFilter,
    selectedPaymentStatus,
    fromDate,
    toDate,
  ]);

  // SORT HOOK (MAIN ADDITION)
  const { sortedData, sortConfig, handleSort } = useSortableTable(
    filteredStudentsPayments,
  );

  const clearFiltersStudent = () => {
    setPaymentCourseFilter("All Courses");
    setSelectedStudentFilter("All Students");
    setSelectedPaymentStatus("All Payments");
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
  };

  const handleExportStudentPayments = () => {
    const formattedData = (sortedData || []).map((a) => ({
      Register_ID: a.student_reg_number || "N/A",
      Name: a.full_name || "N/A",
      Email: a.email || "N/A",
      Phone: a.phone_number || "N/A",
      Course: a.course_title || "N/A",
      Date: a.submitted_at || "N/A",
      "Course Price": a.order_amount || "N/A",
      "Payment Type": a.payment_method || "N/A",
      "Order Id": a.order_id || "N/A",
      "Transaction Id": a.transaction_id || "N/A",
      "Invoice Id": a.invoice_number || "N/A",
      Status: a.status || "N/A",
    }));

    exportToExcel({
      data: formattedData,
      fileName: "Student_Payments",
      sheetName: "Payments",
    });
  };

  // Pagination slice
  const rowsPerPage = 5;
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const indexOfLastStudent = currentPage * rowsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - rowsPerPage;

  const currentStudentsPayments = sortedData.slice(
    indexOfFirstStudent,
    indexOfLastStudent,
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [
    paymentCourseFilter,
    selectedStudentFilter,
    selectedPaymentStatus,
    fromDate,
    toDate,
  ]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages || 1);
    }
  }, [totalPages, currentPage]);

  //  SORT ICON HELPER
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort size={11} />;
    if (sortConfig.direction === "asc") return <FaSortUp size={11} />;
    return <FaSortDown size={11} />;
  };

  // function call
  const pages = getPageNumbers(currentPage, totalPages);

  if (loadingStudentPayments) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container className="admin-platform-analytics-wrapper">
      <h4 className="admin-dashboard-title">Student Payments Details</h4>
      <p className="admin-dashboard-subtitle">
View student payment records, transaction details, and payment status. 
      </p>

      <Row className="admin-analytics-section-cards-row">
        <Col lg={3} md={4} sm={6} xs={12} className="mb-3">
          <div className="admin-analytics-section-stat-card">
            <h6 className="admin-analytics-section-stat-title">
              Enrolled Students
            </h6>
            <img
              src={totalStudents}
              className="admin-analytics-stat-icon"
              alt="students"
            />

            <h3 className="admin-analytics-section-stat-value">
              {Number(paymentStats.total_students) || "-"}
            </h3>
            <span className="admin-analytics-section-stat-sub">
              158 Active • 37 Completed
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="admin-analytics-section-stat-card">
            <h6 className="admin-analytics-section-stat-title">
              Total Course Purchases
            </h6>
            <img
              src={totalCoursePurcha}
              className="admin-analytics-stat-icon"
              alt="courses"
            />

            <h3 className="admin-analytics-section-stat-value">
              {Number(paymentStats.total_purchased_courses) || "-"}
            </h3>
            <span className="admin-analytics-section-stat-sub">
              Avg Rating: 4.6
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="admin-analytics-section-stat-card">
            <h6 className="admin-analytics-section-stat-title">
              Weekly Course Purchases
            </h6>
            <img
              src={weeklyCoursePurchas}
              className="admin-analytics-stat-icon"
              alt="course"
            />

            <h3 className="admin-analytics-section-stat-value">
              {Number(paymentStats.last_week_purchases) || "-"}
            </h3>
            <span className="admin-analytics-section-stat-sub">
              38 Published • 7 Draft
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="admin-analytics-section-stat-card">
            <h6 className="admin-analytics-section-stat-title">
              Total Revenue
            </h6>
            <img
              src={totalRevenue}
              className="admin-analytics-stat-icon"
              alt="revenue"
            />

            <h3 className="admin-analytics-section-stat-value">
              {Number(paymentStats.total_revenue) || "-"}
            </h3>
            <span className="admin-analytics-section-stat-sub">
              $58K this month
            </span>
          </div>
        </Col>
      </Row>

      <Card className="admin-platform-analytics-tutor-table-card">
        <Card.Body className="p-0">
          <div className="d-flex justify-content-between align-items-center">
            {/* <h5 className="admin-platform-analytics-table-title">
              Student Payments
            </h5> */}

            <div className="d-flex gap-3 admin-dashboard-student-payments-input">
              <Form.Select
                // size="sm"
                value={paymentCourseFilter}
                onChange={(e) => setPaymentCourseFilter(e.target.value)}
                className="tutor-analytics-section-student-filter tutor-analytics-section-topcourses-filter"
              >
                <option value="All Courses">All Courses</option>
                {filteredCoursesForDropdown.map((course, index) => (
                  <option key={index} value={course}>
                    {course}
                  </option>
                ))}
              </Form.Select>

              <Form.Select
                // size="sm"
                value={selectedStudentFilter}
                onChange={(e) => setSelectedStudentFilter(e.target.value)}
                className="tutor-analytics-section-student-filter tutor-analytics-section-topcourses-filter"
              >
                <option value="All Students">All Students</option>
                {filteredStudentsForDropdown.map((student, index) => (
                  <option key={index} value={student}>
                    {student}
                  </option>
                ))}
              </Form.Select>

              <Form.Select
                // size="sm"
                value={selectedPaymentStatus}
                onChange={(e) => setSelectedPaymentStatus(e.target.value)}
                className="tutor-analytics-section-student-filter tutor-analytics-section-topcourses-filter"
              >
                <option value="All Payments">All Payments</option>
                {filteredPaymentStatusForDropdown.map((status, index) => (
                  <option key={index} value={status}>
                    {status}
                  </option>
                ))}
              </Form.Select>

              <Form.Control
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="tutor-analytics-section-student-filter tutor-analytics-section-topcourses-filter"
              />

              <Form.Control
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="tutor-analytics-section-student-filter tutor-analytics-section-topcourses-filter"
              />

              <Button
                // size="sm"
                className="tutor-analytics-section-student-export"
                onClick={clearFiltersStudent}
              >
                Clear
              </Button>

              <Button
                size="sm"
                className="admin-platform-analytics-table-export me-2"
                onClick={handleExportStudentPayments}
                disabled={sortedData.length === 0}
                title={
                  sortedData.length === 0
                    ? "No data available to export"
                    : "Export to Excel"
                }
                style={{
                  cursor: sortedData.length === 0 ? "not-allowed" : "pointer",
                  opacity: sortedData.length === 0 ? 0.5 : 1,
                }}
              >
                <img
                  src={excelImg}
                  alt="Excel"
                  className="admin-dashboard-table-export-excel-img"
                  style={{ marginBottom: 0 }}
                />
              </Button>
            </div>
          </div>

          {/* Table Header */}
          <div className="admin-assignment-management-table-wrapper">
            <Table className="admin-assignment-management-table">
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort("student_reg_number")}
                    style={{ cursor: "pointer" }}
                  >
                    Enroll Id {renderSortIcon("student_reg_number")}
                  </th>
                  <th
                    onClick={() => handleSort("full_name")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="admin-dashboard-sort-header">
                      Student {renderSortIcon("full_name")}{" "}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("email")}
                    style={{ cursor: "pointer" }}
                  >
                    Email {renderSortIcon("email")}
                  </th>
                  <th>Phone</th>
                  <th
                    onClick={() => handleSort("course_title")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="admin-dashboard-sort-header">
                      Course {renderSortIcon("course_title")}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("submitted_at")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="admin-dashboard-sort-header">
                      Date {renderSortIcon("submitted_at")}
                    </div>
                  </th>
                  <th>Price</th>
                  <th
                    onClick={() => handleSort("payment_method")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="admin-dashboard-sort-header">
                      Pay Type {renderSortIcon("payment_method")}
                    </div>
                  </th>

                  <th>
                    <div className="admin-dashboard-sort-header">Order ID</div>
                  </th>
                  <th>
                    <div className="admin-dashboard-sort-header">
                      Transaction ID
                    </div>
                  </th>
                  <th>
                    <div className="admin-dashboard-sort-header">
                      Invoice Id
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("status")}
                    style={{ cursor: "pointer" }}
                  >
                    Status {renderSortIcon("status")}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentStudentsPayments.length > 0 ? (
                  currentStudentsPayments.map((student) => (
                    <tr key={student.student_course_id}>
                      <td title={student.student_reg_number}>
                        {student.student_reg_number || "-"}
                      </td>
                      <td title={student.full_name}>
                        {student.full_name || "-"}
                      </td>
                      <td title={student.email}>{student.email || "-"}</td>
                      <td title={student.phone_number}>
                        {student.phone_number || "-"}
                      </td>
                      <td title={student.course_title}>
                        {student.course_title || "-"}
                      </td>
                      <td title={student.submitted_at}>
                        {student.submitted_at || "-"}
                      </td>
                      <td title={student.order_amount}>
                        {student.order_amount || "-"}
                      </td>
                      <td title={student.payment_method}>
                        {student.payment_method || "-"}
                      </td>
                      <td title={student.order_id}>
                        {student.order_id || "-"}
                      </td>
                      <td title={student.transaction_id}>
                        {student.transaction_id || "-"}
                      </td>
                      <td title={student.invoice_number}>
                        {student.invoice_number || "-"}
                      </td>
                      <td title={student.status}>
                        <span
                          className={
                            student.status?.toLowerCase() === "success"
                              ? "student-purshace-section-badge-success"
                              : student.status?.toLowerCase() === "failed"
                                ? "student-purshace-section-badge-failed"
                                : "student-purshace-section-badge-pending"
                          }
                        >
                          {student.status || "-"}
                        </span>
                      </td>
                      <td>
                        <div className="admin-platform-analytics-action-icons">
                          <FaEye
                            title={
                              student.status?.toLowerCase() === "pending"
                                ? "Invoice not available yet"
                                : "View Invoice"
                            }
                            className={`admin-platform-analytics-eye ${
                              student.status === "PENDING"
                                ? "disabled-action-admin-dashbaord"
                                : ""
                            }`}
                            onClick={() => {
                              if (student.status === "PENDING") return;
                              handleInvoiceView(student.student_course_id);
                            }}
                          />
                          <FiDownload
                            title={
                              student.status?.toLowerCase() === "pending"
                                ? "Invoice not available yet"
                                : "download Invoice"
                            }
                            className={`admin-platform-analytics-eye ${
                              student.status === "PENDING"
                                ? "disabled-action-admin-dashbaord"
                                : ""
                            }`}
                            onClick={() => {
                              if (student.status === "PENDING") return;
                              handleInvoiceDownload(student.student_course_id);
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="12" style={{ textAlign: "center" }}>
                      <img
                        src={Nofound}
                        alt="No Data Found"
                        className="admin-all-tutor-empty-img"
                      />
                      <p className="admin-dashboard-no-data">No Data Found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="tutor-assignment-table-pagination">
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>

          {pages.map((page, idx) =>
            page === "..." ? (
              <span key={idx} style={{ padding: "0 3px" }}>
                ...
              </span>
            ) : (
              <Button
                key={idx}
                active={currentPage === page}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </Button>
            ),
          )}

          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {/* Hidden Invoice For PDF */}
      {downloadInvoice && (
        <div
          style={{
            position: "fixed",
            top: "-10000px",
            left: "-10000px",
            background: "#ffffff",
          }}
        >
          <div
            ref={invoicePdfRef}
            className="student-purshace-invoice-wrapper student-purshace-pdf-download-mode"
          >
            <div className="student-purshace-invoice-top">
              <div className="student-purshace-invoice-left">
                <div className="student-purshace-invoice-logo">
                  <img
                    src={Logo}
                    alt="skilledition"
                    className="student-purshace-logo-img"
                  />
                </div>
                <p className="student-purshace-invoice-powered">
                  Powered by Digisphere
                </p>

                <p className="student-purshace-mt-20">
                  Ramavarappadu,
                  <br />
                  Vijayawada,
                  <br />
                  Andhra Pradesh, 520010
                  <br />
                  Telephone: (+91) 814-387-9627
                </p>

                <p className="student-purshace-invoice-to">
                  <strong>Invoice To</strong>
                  <br />
                  {downloadInvoice.full_name || "-"}
                </p>
              </div>

              <div className="student-purshace-invoice-download-right">
                <p className="student-purshace-invoice-number">
                  Invoice ID - {downloadInvoice.invoice_number || "-"}
                </p>

                <p className="student-purshace-invoice-number">
                  Order ID - {downloadInvoice.order_id || "-"}
                </p>
                <p className="student-purshace-invoice-number">
                  Transaction ID - {downloadInvoice.transaction_id || "-"}
                </p>

                <p className="student-dash-purshace-date">
                  Date: {downloadInvoice.purchase_date || "-"}
                </p>

                <div className="student-purshace-payment-details">
                  <h6>Payment Details:</h6>

                  <p>Payment Type : {downloadInvoice.payment_method || "-"}</p>

                  <p>
                    Payment Status :
                    <span
                      className={`ms-2 ${
                        downloadInvoice.status?.toLowerCase() === "success"
                          ? "student-purshace-section-badge-success"
                          : downloadInvoice.status?.toLowerCase() === "failed"
                            ? "student-purshace-section-badge-failed"
                            : "student-purshace-section-badge-pending"
                      }`}
                    >
                      {downloadInvoice.status || "-"}
                    </span>
                  </p>
                  <p>Email : {downloadInvoice.email || "-"}</p>

                  <p>Phone : {downloadInvoice.phone_number || "-"}</p>
                  {/* <p>Country : India</p>
                  <p>City : Vijayawada</p> */}
                  <p>Address : {downloadInvoice.address || "-"}</p>
                  <p>Pincode : {downloadInvoice.pincode || "-"}</p>
                </div>
              </div>
            </div>

            <div className="student-purshace-invoice-table-wrapper">
              <table className="student-purshace-invoice-table">
                <thead>
                  <tr>
                    <th>Course</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td> {downloadInvoice.course_title || "-"}</td>
                    <td>01</td>
                    <td>₹ {downloadInvoice.order_amount || 0}</td>
                    <td className="student-purshace-text-blue">
                      ₹ {downloadInvoice.order_amount || 0}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="student-purshace-total-section">
              <div className="student-purshace-total-row">
                <span>Sub-Total Amount</span>
                <span>₹ {downloadInvoice.order_amount || 0}</span>
              </div>

              {/* <div className="student-purshace-total-row">
                <span>GST (18%)</span>
                <span>
                  ₹{(Number(downloadInvoice.amount) * 0.18).toFixed(2)}
                </span>
              </div> */}

              <div className="student-purshace-total-row student-purshace-grand-total">
                <span>Grand Total</span>
                <span>
                  {/* ₹{(Number(downloadInvoice.amount) * 1.18).toFixed(2)} */}
                  ₹ {Number(downloadInvoice.order_amount || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* modal data view  */}
      <Modal
        show={isInvoiceModalOpen}
        onHide={() => setIsInvoiceModalOpen(false)}
        size="xl"
        centered
        className="student-purshace-modal"
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader closeButton className="student-purshace-modal-header">
          <h6 className="student-purshace-modal-title">Invoice Info</h6>
        </ModalHeader>

        <Modal.Body className="p-0">
          {selectedInvoice && (
            <div
              ref={invoiceModalRef}
              className="student-purshace-invoice-wrapper"
            >
              <div className="student-purshace-invoice-top">
                <div className="student-purshace-invoice-left">
                  <div className="student-purshace-invoice-logo">
                    <img
                      src={Logo}
                      alt="skilledition"
                      className="student-purshace-logo-img"
                    />
                  </div>
                  <p className="student-purshace-invoice-powered">
                    Powered by Digisphere
                  </p>

                  <p className="student-purshace-mt-20">
                    Ramavarappadu,
                    <br />
                    Vijayawada,
                    <br />
                    Andhra Pradesh, 520010
                    <br />
                    Telephone: (+91) 814-387-9627
                  </p>

                  <p className="student-purshace-invoice-to">
                    <strong>Invoice To</strong>
                    <br />
                    {selectedInvoice.full_name || "-"}
                  </p>
                </div>

                <div className="student-purshace-invoice-right">
                  <p className="student-purshace-invoice-number">
                    Invoice ID - {selectedInvoice.invoice_number || "-"}
                  </p>

                  <p className="student-purshace-invoice-number">
                    Order ID - {selectedInvoice.order_id || "-"}
                  </p>
                  <p className="student-purshace-invoice-number">
                    Transaction ID - {selectedInvoice.transaction_id || "-"}
                  </p>

                  <p className="student-dash-purshace-date">
                    Date: {selectedInvoice.purchase_date || "-"}
                  </p>

                  <div className="student-purshace-payment-details">
                    <h6>Payment Details:</h6>
                    <p>
                      Payment Type : {selectedInvoice.payment_method || "-"}
                    </p>
                    <p>
                      Payment Status :
                      <span
                        className={`ms-2 ${
                          selectedInvoice.status?.toLowerCase() === "success"
                            ? "student-purshace-section-badge-success"
                            : selectedInvoice.status?.toLowerCase() === "failed"
                              ? "student-purshace-section-badge-failed"
                              : "student-purshace-section-badge-pending"
                        }`}
                      >
                        {selectedInvoice.status || "-"}
                      </span>
                    </p>
                    <p>Email : {selectedInvoice.email || "-"}</p>
                    <p>Phone : {selectedInvoice.phone_number || "-"}</p>
                    <p>Address : {selectedInvoice.address || "-"}</p>
                    <p>Pincode : {selectedInvoice.pincode || "-"}</p>
                  </div>
                </div>
              </div>

              <div className="student-purshace-invoice-table-wrapper">
                <table className="student-purshace-invoice-table">
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td> {selectedInvoice.course_title || "-"}</td>
                      <td>01</td>
                      <td> ₹ {selectedInvoice.order_amount || 0}</td>
                      <td className="student-purshace-text-blue">
                        ₹ {selectedInvoice.order_amount || 0}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="student-purshace-total-section">
                <div className="student-purshace-total-row">
                  <span>Sub-Total Amount</span>
                  <span>₹ {selectedInvoice.order_amount || 0}</span>
                </div>

                {/* <div className="student-purshace-total-row">
                  <span>GST (18%)</span>
                  <span>
                    ₹{(Number(selectedInvoice.amount) * 0.18).toFixed(2)}
                  </span>
                </div> */}

                <div className="student-purshace-total-row student-purshace-grand-total">
                  <span>Grand Total</span>
                  {/* <span>₹{(selectedInvoice.amount * 1.18).toFixed(2)}</span> */}
                  <span>₹ {selectedInvoice.order_amount || 0}</span>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AdminStudentPayments;
